import { cva, VariantProps } from 'class-variance-authority';

import { BonusIconLight } from '../../components/bonus/bonus-icon-light';
import { Typography } from '../../components/typography/typography';
import { Skeleton } from '../../lib/shadcn/components/ui/skeleton';
import { Stack } from '../../lib/shadcn/components/ui/stack/stack';
import { cn } from '../../lib/shadcn/lib/utils';

type BonusChipProps = {
  bonuses: number;
  prefix?: string;
  size?: 'md' | 'sm' | 'xs';
  isLoading?: boolean;
  className?: string;
  dataTestId?: string;
};

const chipVariants = cva('w-fit bg-accent-500', {
  variants: {
    size: {
      md: 'p-unit-1 pl-unit-3',
      sm: 'p-unit-1 pl-unit-2',
      xs: 'h-[15px] py-0 pl-1 pr-0.5 leading-none',
    },
    rounded: {
      small: 'rounded-small',
      full: 'rounded-full',
    },
  },
  defaultVariants: {
    size: 'md',
    rounded: 'small',
  },
});

export function BonusChip({
  bonuses,
  prefix = '',
  size = 'md',
  rounded = 'small',
  isLoading = false,
  className,
  dataTestId,
}: BonusChipProps & VariantProps<typeof chipVariants>) {
  if (isLoading) {
    return (
      <Skeleton className={cn('h-[20px] w-[40px] rounded-xl bg-accent-500', className)} />
    );
  }

  const isPill = rounded === 'full' && (size === 'xs' || size === 'sm');
  const TextTypography = size === 'md' ? Typography.p4 : Typography.small1;

  if (isPill) {
    const isSm = size === 'sm';
    return (
      <div
        className={cn(
          'inline-flex items-center rounded-[6px] bg-accent-500 leading-none',
          isSm ? 'h-[17px] gap-1 pl-1.5 pr-0.5' : 'h-[15px] gap-1 pl-1 pr-0.5',
          className,
        )}
        data-testid={dataTestId}
      >
        <span
          data-testid="bonus-chip"
          className={cn(
            'font-normal leading-none tracking-[-0.01em] text-white',
            isSm ? 'text-[11px]' : 'text-[9px]',
          )}
        >
          {prefix ? `${prefix} ` : ''}
          {Math.trunc(bonuses)}
        </span>
        <svg
          aria-hidden="true"
          className={cn('shrink-0', isSm ? 'size-[13px]' : 'size-[11px]')}
          viewBox="0 0 16 16"
          fill="none"
        >
          <rect width="16" height="16" rx="6" className="fill-accent-100" />
          <path
            d="M12.7866 12.4113L12.6306 12.8923C12.4053 12.9096 12.2363 12.9183 12.1236 12.9183C11.6036 12.9183 11.1789 12.6973 10.8496 12.2553C10.5289 11.822 10.3686 11.2543 10.3686 10.5523C10.3686 10.1276 10.4639 9.326 10.6546 8.14731C10.8539 6.95998 11.0836 5.87664 11.3436 4.89731C11.3956 4.68064 11.4216 4.53764 11.4216 4.46831C11.4216 4.39898 11.3913 4.36431 11.3306 4.36431C11.2699 4.36431 11.0749 4.68931 10.7456 5.33931C10.4249 5.98065 10.0393 6.77364 9.5886 7.71831C9.1379 8.6543 8.8779 9.19164 8.8086 9.33031C8.7479 9.46031 8.6959 9.56864 8.6526 9.65531C8.6179 9.73331 8.5399 9.88064 8.4186 10.0973C8.3059 10.3053 8.1976 10.4916 8.0936 10.6563C7.8076 11.0896 7.5823 11.3063 7.4176 11.3063C7.3049 11.3063 7.1793 11.276 7.0406 11.2153C6.9106 11.1546 6.8239 11.0896 6.7806 11.0203C6.6853 10.873 6.6376 10.4136 6.6376 9.64231C6.6376 8.03031 6.7676 6.49198 7.0276 5.02731C7.1143 4.54198 7.1576 4.28631 7.1576 4.26031C7.1576 4.17364 7.1229 4.13031 7.0536 4.13031C6.9149 4.13031 6.6939 4.56364 6.3906 5.43031L6.0136 6.50931C5.4589 8.07801 4.8956 9.30001 4.3236 10.1753C3.7603 11.0506 3.1103 11.666 2.3736 12.0213L2.0356 11.3063C2.8763 10.6823 3.5219 10.0453 3.9726 9.39531C4.4319 8.74531 4.9303 7.69664 5.4676 6.24931L5.9096 5.06631C6.2129 4.24298 6.4773 3.65798 6.7026 3.31131C6.9279 2.95598 7.1793 2.77831 7.4566 2.77831C7.9159 2.77831 8.1456 3.05564 8.1456 3.61031C8.1456 3.65364 8.0633 4.53331 7.8986 6.24931C7.7339 7.95665 7.6516 9.08331 7.6516 9.62931C7.6516 9.78531 7.6776 9.86764 7.7296 9.87631C7.7729 9.87631 7.8206 9.833 7.8726 9.74631C8.5746 8.57631 9.2463 7.30231 9.8876 5.92431C9.9656 5.75098 10.0999 5.45198 10.2906 5.02731C10.4813 4.60264 10.6676 4.22564 10.8496 3.89631C11.0316 3.56698 11.1703 3.36764 11.2656 3.29831C11.3609 3.22031 11.5126 3.18131 11.7206 3.18131C11.9286 3.18131 12.1063 3.25931 12.2536 3.41531C12.4096 3.56264 12.4876 3.74031 12.4876 3.94831C12.4876 4.15631 12.4573 4.39464 12.3966 4.66331C11.7206 7.96531 11.3826 9.94131 11.3826 10.5913C11.3826 11.2326 11.4866 11.6703 11.6946 11.9043C11.9026 12.147 12.2666 12.316 12.7866 12.4113Z"
            className="fill-accent-500"
          />
        </svg>
      </div>
    );
  }

  return (
    <Stack
      align="center"
      justify="center"
      direction="row"
      gap={1}
      className={cn(chipVariants({ size, rounded }), className)}
      data-testid={dataTestId}
    >
      <TextTypography data-testid="bonus-chip" className="text-white">
        {prefix ? `${prefix} ` : ''}
        {Math.trunc(bonuses)}
      </TextTypography>
      <BonusIconLight
        className={cn({
          'size-[16px]': size === 'md',
          'size-[14px]': size === 'sm',
        })}
      />
    </Stack>
  );
}
